import { Location } from '@angular/common';
import { HttpHandler, HttpRequest } from '@angular/common/http';
import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalInterceptorAuthRequest,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  InteractionType,
  RedirectRequest,
} from '@azure/msal-browser';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { UserRoles } from 'src/app/core/enums';
import { environment } from 'src/environments/environment';
import { UserService } from '../../services/user/user.service';
import { CommonUtils } from '../../utils/common/common.utils';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService implements OnDestroy {
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private userService = inject(UserService);
  private commonUtils = inject(CommonUtils);
  private location = inject(Location);
  private msalInterceptorConfig = inject<MsalInterceptorConfiguration>(
    MSAL_INTERCEPTOR_CONFIG,
  );
  private router = inject(Router);

  isIframe!: boolean;

  private state!: string;

  isDrawerOpen$ = new BehaviorSubject(false);

  isValidSession = signal(false);
  isReturningUser = signal(false);
  allowDrawerToggle = signal(false);

  private destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  init(): void {
    this.isIframe = window !== window.parent && !window.opener;
    this.state = window.location.href;
    this.msalService.instance.enableAccountStorageEvents();
    this.msalBroadcastSubject();
    this.msalBroadcastInProgress();
  }

  handleRedirect(): Observable<boolean> {
    // do this until an account is emitted
    return this.msalService.handleRedirectObservable().pipe(
      catchError((error) => {
        if (error?.errorMessage?.includes('AADB2C90118')) {
          this.msalService.loginRedirect({
            authority:
              'https://upsb2c.b2clogin.com/upsb2c.onmicrosoft.com/B2C_1_Tundra_ResetPwd',
            scopes: ['openid'],
            redirectUri: environment.redirectURI,
          });
          return EMPTY;
        }
        return of(false);
      }),
      mergeMap(() => {
        if (!this.msalService.instance.getAllAccounts().length) {
          if (this.state) {
            //First Time Login, route to login page
            this.router.navigate(['login']);
          }
          return of(false);
        }

        const account = this.msalService.instance.getActiveAccount();

        if (account != null) {
          //Check if session is valid, because it was previously checked,
          //If it is valid then we can skip all the other checks and just return true
          if (this.isValidSession() === false) {
            return this.firstTimeLogin();
          } else {
            return of(true);
          }
        } else {
          return of(false);
        }
      }),
      catchError(() => {
        return of(false);
      }),
    );
  }

  loginInteractively(): Observable<boolean> {
    const redirectURI = this.getDestinationUrl();

    sessionStorage.removeItem('msal.interaction.status');

    const redirectRequest = {
      redirectStartPage: redirectURI,
      scopes: environment.B2E_scope,
    } as RedirectRequest;

    this.msalService.loginRedirect(redirectRequest).pipe(take(1)).subscribe();

    return of(false);
  }

  getDestinationUrl(): string {
    const baseElements = document.getElementsByTagName('base');
    const baseUrl = this.location.normalize(
      baseElements.length ? baseElements[0].href : window.location.origin,
    );
    this.commonUtils.updateBanner(false);
    return `${baseUrl}`;
  }

  getAuthRequest(
    account: AccountInfo,
    request?: HttpRequest<unknown>,
  ): MsalInterceptorAuthRequest {
    if (
      typeof this.msalInterceptorConfig.authRequest === 'function' &&
      request &&
      account
    ) {
      return this.msalInterceptorConfig.authRequest(this.msalService, request, {
        account: account,
      });
    } else {
      return { ...this.msalInterceptorConfig.authRequest, account };
    }
  }

  acquireToken(
    request: HttpRequest<unknown>,
    scopes: string[],
    next: HttpHandler,
  ): Observable<AuthenticationResult> {
    const getActiveAccount = this.msalService.instance.getActiveAccount();
    const account = getActiveAccount ? getActiveAccount : ({} as AccountInfo);
    const authRequest = this.getAuthRequest(account);
    return this.msalService
      .acquireTokenSilent({
        ...authRequest,
        scopes,
        forceRefresh: true,
        account,
      })
      .pipe(
        take(1),
        catchError(() => {
          return this.msalBroadcastService.inProgress$.pipe(
            take(1),
            switchMap((status: InteractionStatus) => {
              if (status === InteractionStatus.None) {
                return this.acquireTokenInteractively(authRequest, scopes);
              }

              return this.msalBroadcastService.inProgress$.pipe(
                filter(
                  (interactionStatus: InteractionStatus) =>
                    interactionStatus === InteractionStatus.None,
                ),
                take(1),
                switchMap(() => this.acquireToken(request, scopes, next)),
              );
            }),
          );
        }),
        switchMap((result: AuthenticationResult) => {
          if (!result.accessToken) {
            return this.msalBroadcastService.inProgress$.pipe(
              filter(
                (status: InteractionStatus) =>
                  status === InteractionStatus.None,
              ),
              take(1),
              switchMap(() =>
                this.acquireTokenInteractively(authRequest, scopes),
              ),
            );
          }
          return of(result);
        }),
      );
  }

  setMsalConfig(): MsalGuardConfiguration {
    return {
      interactionType: InteractionType.Redirect,
      authRequest: {
        scopes: environment.B2E_scope,
      },
    };
  }

  private setActiveAccount() {
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      const accounts = this.msalService.instance.getAllAccounts();
      activeAccount = accounts?.length > 0 ? accounts[0] : null;
      this.msalService.instance.setActiveAccount(activeAccount);
    }
  }

  private msalBroadcastSubject() {
    this.msalBroadcastService.msalSubject$
      .pipe(
        takeUntil(this.destroy$),
        filter((msg: EventMessage) => {
          return (
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
          );
        }),
        map(() => {
          // redirect to baseUrl if no account found
          if (this.msalService.instance.getAllAccounts().length === 0) {
            window.location.pathname = '/';
          }
        }),
      )
      .subscribe();
  }

  private msalBroadcastInProgress() {
    this.msalBroadcastService.inProgress$
      .pipe(
        takeUntil(this.destroy$),
        map((status) => {
          if (status === InteractionStatus.None) {
            this.setActiveAccount();
          }
        }),
      )
      .subscribe();
  }

  private acquireTokenInteractively(
    authRequest: MsalInterceptorAuthRequest,
    scopes: string[],
  ): Observable<AuthenticationResult> {
    const redirectStartPage = window.location.href;
    this.msalService.acquireTokenRedirect({
      ...authRequest,
      scopes,
      redirectStartPage,
    });
    return EMPTY;
  }

  private firstTimeLogin() {
    return this.userService.getUser().pipe(
      take(1),
      catchError((error) => {
        const statusCode = error?.status ? error.status : 0;
        if (statusCode > 0 && statusCode === 400) {
          this.isReturningUser.set(true);
        }
        this.commonUtils.lockUpdateLoadingModal(false);
        this.commonUtils.updateLoadingModal(false);
        this.isValidSession.set(false);
        return of(null);
      }),
      filter((val) => !!val),
      map((getUser) => {
        this.commonUtils.updateLoadingModal(false);
        if (getUser?.response?.user) {
          const user = getUser.response.user;
          const existingRoles: string[] = Object.values(UserRoles);
          if (existingRoles.includes(user?.roleName)) {
            this.userService.setCurrentUser$(user);
            this.allowDrawerToggle.set(true);
            this.isValidSession.set(true);
            return true;
          } else {
            this.isValidSession.set(false);
            return false;
          }
        } else {
          this.isValidSession.set(false);
          return false;
        }
      }),
    );
  }
}
